import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Alert,
  CircularProgress,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';

const OtherHistoryPage = () => {
  const { t } = useTranslation('masterchart');
  const { user } = useContext(AuthContext);

  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Translation keys rather than text, so a language switch re-renders them.
  const [successMsg, setSuccessMsg] = useState(null);

  const { control, getValues, reset } = useForm({
    defaultValues: {
      smokingStatus: '',
      alcoholUse: '',
      drugUse: '',
      occupation: '',
      familyHistory: '',
    },
  });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      const other = hr.other || {};
      reset({
        smokingStatus: other.smokingStatus || '',
        alcoholUse: other.alcoholUse || '',
        drugUse: other.drugUse || '',
        occupation: other.occupation || '',
        familyHistory: other.familyHistory || '',
      });
    } catch {
      // Blank fields after a failed load look like an empty record, and saving
      // them would overwrite the real one, so the form stays hidden until a retry.
      setFullHealthRecords(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, reset]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!user?.uid || saving || fullHealthRecords == null) return;
    setSaving(true);
    setError(null);
    try {
      const values = getValues();
      const merged = {
        ...(fullHealthRecords ?? {}),
        other: values,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
      setSuccessMsg('common.saved');
    } catch {
      setError('common.failedSave');
    } finally {
      setSaving(false);
    }
  }, [user?.uid, saving, getValues, fullHealthRecords]);

  if (loading) {
    return (
      <div className="inner">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title={t('otherHistory.title')}
        subtitle={t('otherHistory.subtitle')}
      />

      {loadFailed && (
        <DataLoadErrorAlert
          message={t('otherHistory.loadError')}
          onRetry={load}
        />
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}
      {successMsg && (
        <Alert
          severity="success"
          onClose={() => setSuccessMsg(null)}
          sx={{ mb: 2 }}
        >
          {t(successMsg)}
        </Alert>
      )}

      {!loadFailed && (
        <>
          {/* Flush with the heading like every other MasterChart page: no padded
              Paper, bold section headings. */}
          <Box component="section" sx={{ mb: 4 }}>
            <Typography variant="subtitle2" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
              {t('otherHistory.social')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="smokingStatus"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('otherHistory.smoking')}
                      fullWidth
                      placeholder={t('otherHistory.smokingPlaceholder')}
                      helperText={t('otherHistory.smokingHelp')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="alcoholUse"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('otherHistory.alcohol')}
                      fullWidth
                      placeholder={t('otherHistory.alcoholPlaceholder')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="drugUse"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('otherHistory.drugs')}
                      fullWidth
                      placeholder={t('otherHistory.drugsPlaceholder')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="occupation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('otherHistory.occupation')}
                      fullWidth
                      placeholder={t('otherHistory.occupationPlaceholder')}
                      helperText={t('otherHistory.occupationHelp')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Box component="section" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" component="h3" fontWeight="bold" sx={{ mb: 2 }}>
              {t('otherHistory.family')}
            </Typography>
            <Box>
              <Controller
                name="familyHistory"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('otherHistory.familyLabel')}
                    fullWidth
                    multiline
                    rows={5}
                    placeholder={t('otherHistory.familyPlaceholder')}
                    helperText={t('otherHistory.familyHelp')}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={saving}
              disabled={saving}
            >
              {t('common.save')}
            </LoadingButton>
            <MasterChartBackButton />
          </Box>
        </>
      )}
    </div>
  );
};

export default OtherHistoryPage;
